// Keyboard shortcuts guide — the `?` shortcut list and the editor shortcuts it
// documents. Locale-keyed like every guide (content.<lang> → { feature, title,
// sections }).

const keyboardShortcutsGuide = {
  id: "keyboard-shortcuts",
  icon: "keyboard",
  keywords: [
    "keyboard",
    "klavye",
    "shortcut",
    "shortcuts",
    "kısayol",
    "kısayollar",
    "hotkey",
    "kısa yol",
    "undo",
    "geri al",
    "redo",
    "yinele",
    "copy",
    "kopyala",
    "paste",
    "yapıştır",
    "cut",
    "kes",
    "duplicate",
    "çoğalt",
    "select all",
    "tümünü seç",
    "delete",
    "sil",
    "nudge",
    "arrow keys",
    "ok tuşları",
    "aspect ratio",
    "en-boy oranı",
    "preview",
    "önizleme",
    "print",
    "yazdır",
    "pdf",
    "ctrl",
    "cmd",
    "multi-select",
    "çoklu seçim",
    "teclado",
    "atajo",
    "atajos",
    "atajos de teclado",
    "deshacer",
    "rehacer",
    "copiar",
    "pegar",
    "cortar",
    "duplicar",
    "seleccionar todo",
    "eliminar",
    "teclas de flecha",
    "vista previa",
    "imprimir",
  ],
  content: {
    en: {
      feature: "Canvas",
      title: "Keyboard shortcuts",
      sections: [
        {
          id: "opening",
          title: "Opening the shortcut list",
          videoUrl: null,
          text:
            "Press **?** anywhere in the editor to open the **Keyboard shortcuts** list, and press it again (or **Esc**) to close it. You can also click the small round **?** button in the bottom-right corner, just above the assistant launcher.\n\n" +
            "- The list only shows shortcuts that work in Studio today, grouped into **General**, **Editing**, **Selection & arrangement** and **Scene list**.\n" +
            "- On a Mac, rows that accept the Command key show **⌘** instead of **Ctrl**. Rows that still show **Ctrl** on a Mac need the Control key.\n" +
            "- **?** does nothing while you are typing in a text field, so you can still type a question mark.",
        },
        {
          id: "editing",
          title: "Editing shortcuts",
          videoUrl: null,
          text:
            "- **Ctrl+Z** undoes your last change; **Ctrl+Shift+Z** or **Ctrl+Y** redoes it.\n" +
            "- **Ctrl+C** copies the selected element. With nothing selected, it copies the whole scene so you can paste it as a new scene.\n" +
            "- **Ctrl+X** cuts the selected element, and **Ctrl+V** pastes a copied element or scene. Pasting an image, a link or text from another app adds it to the scene as a new element.\n" +
            "- **Ctrl+Alt+E** duplicates the selected element.\n" +
            "- **Delete** or **Backspace** removes the selected element, or every element in a multi-selection.\n\n" +
            "While you are typing inside a text field or editing text, undo and copy/paste work on the text itself, and **Delete** or **Backspace** removes characters instead of the element.",
        },
        {
          id: "selection",
          title: "Selecting and arranging elements",
          videoUrl: null,
          text:
            "- **Ctrl+A** selects every element on the current scene.\n" +
            "- **Ctrl+click** or **Shift+click** adds an element to the selection or removes it.\n" +
            "- **Esc** clears a multi-element selection.\n" +
            "- The **arrow keys** nudge the selected element by 1% of the scene per press.\n" +
            "- Hold **Shift** while dragging a resize handle to lock the element's aspect ratio. If the aspect ratio is already locked, Shift frees it for that resize.",
        },
        {
          id: "scenes-timeline",
          title: "Scenes, timeline and preview",
          videoUrl: null,
          text:
            "- In the scene list, **Ctrl+click** adds a scene to the selection or removes it, **Shift+click** selects every scene between the last one you clicked and this one, and **Esc** clears the scene selection.\n" +
            "- On the timeline, **Delete** or **Backspace** removes the selected clip.\n" +
            "- **F2** opens the course preview.\n" +
            "- **Ctrl+Shift+P** prints the course, or saves it as a PDF from your browser's print window.",
        },
      ],
    },
    tr: {
      feature: "Tuval",
      title: "Klavye kısayolları",
      sections: [
        {
          id: "opening",
          title: "Kısayol listesini açma",
          videoUrl: null,
          text:
            "**Klavye kısayolları** listesini açmak için editörün herhangi bir yerinde **?** tuşuna basın; kapatmak için tekrar basın (veya **Esc**). Sağ alt köşede, asistan başlatıcısının hemen üstündeki küçük yuvarlak **?** düğmesine de tıklayabilirsiniz.\n\n" +
            "- Liste yalnızca Studio'da şu anda çalışan kısayolları gösterir ve bunları **Genel**, **Düzenleme**, **Seçim ve yerleşim** ile **Sahne listesi** başlıkları altında gruplar.\n" +
            "- Mac'te Command tuşunu kabul eden satırlarda **Ctrl** yerine **⌘** görünür. Mac'te hâlâ **Ctrl** gösteren satırlar Control tuşuyla çalışır.\n" +
            "- Bir metin alanına yazarken **?** listeyi açmaz; böylece soru işaretini normal şekilde yazabilirsiniz.",
        },
        {
          id: "editing",
          title: "Düzenleme kısayolları",
          videoUrl: null,
          text:
            "- **Ctrl+Z** son değişikliğinizi geri alır; **Ctrl+Shift+Z** veya **Ctrl+Y** yineler.\n" +
            "- **Ctrl+C** seçili öğeyi kopyalar. Hiçbir şey seçili değilse tüm sahneyi kopyalar; böylece onu yeni bir sahne olarak yapıştırabilirsiniz.\n" +
            "- **Ctrl+X** seçili öğeyi keser, **Ctrl+V** kopyalanan öğeyi veya sahneyi yapıştırır. Başka bir uygulamadan görsel, bağlantı veya metin yapıştırdığınızda sahneye yeni bir öğe olarak eklenir.\n" +
            "- **Ctrl+Alt+E** seçili öğeyi çoğaltır.\n" +
            "- **Delete** veya **Backspace** seçili öğeyi ya da çoklu seçimdeki tüm öğeleri siler.\n\n" +
            "Bir metin alanına yazarken veya metin düzenlerken geri alma ve kopyala/yapıştır metnin kendisi üzerinde çalışır; **Delete** veya **Backspace** öğeyi değil karakterleri siler.",
        },
        {
          id: "selection",
          title: "Öğeleri seçme ve yerleştirme",
          videoUrl: null,
          text:
            "- **Ctrl+A** geçerli sahnedeki tüm öğeleri seçer.\n" +
            "- **Ctrl+tıklama** veya **Shift+tıklama** bir öğeyi seçime ekler ya da seçimden çıkarır.\n" +
            "- **Esc** çoklu öğe seçimini temizler.\n" +
            "- **Ok tuşları** seçili öğeyi her basışta sahnenin %1'i kadar kaydırır.\n" +
            "- Bir yeniden boyutlandırma tutamacını sürüklerken **Shift** tuşunu basılı tutarak öğenin en-boy oranını kilitleyin. En-boy oranı zaten kilitliyse Shift, o boyutlandırma için kilidi kaldırır.",
        },
        {
          id: "scenes-timeline",
          title: "Sahneler, zaman çizelgesi ve önizleme",
          videoUrl: null,
          text:
            "- Sahne listesinde **Ctrl+tıklama** bir sahneyi seçime ekler ya da seçimden çıkarır, **Shift+tıklama** en son tıkladığınız sahne ile bu sahne arasındaki tüm sahneleri seçer, **Esc** ise sahne seçimini temizler.\n" +
            "- Zaman çizelgesinde **Delete** veya **Backspace** seçili klibi siler.\n" +
            "- **F2** eğitimin önizlemesini açar.\n" +
            "- **Ctrl+Shift+P** eğitimi yazdırır; tarayıcınızın yazdırma penceresinden PDF olarak da kaydedebilirsiniz.",
        },
      ],
    },
    es: {
      feature: "Lienzo",
      title: "Atajos de teclado",
      sections: [
        {
          id: "opening",
          title: "Abrir la lista de atajos",
          videoUrl: null,
          text:
            "Pulsa **?** en cualquier lugar del editor para abrir la lista **Atajos de teclado**, y vuelve a pulsarla (o pulsa **Esc**) para cerrarla. También puedes hacer clic en el pequeño botón redondo **?** de la esquina inferior derecha, justo encima del lanzador del asistente.\n\n" +
            "- La lista solo muestra los atajos que funcionan actualmente en Studio, agrupados en **General**, **Edición**, **Selección y organización** y **Lista de escenas**.\n" +
            "- En Mac, las filas que aceptan la tecla Comando muestran **⌘** en lugar de **Ctrl**. Las filas que siguen mostrando **Ctrl** en Mac necesitan la tecla Control.\n" +
            "- **?** no hace nada mientras escribes en un campo de texto, así que puedes seguir escribiendo el signo de interrogación.",
        },
        {
          id: "editing",
          title: "Atajos de edición",
          videoUrl: null,
          text:
            "- **Ctrl+Z** deshace tu último cambio; **Ctrl+Shift+Z** o **Ctrl+Y** lo rehace.\n" +
            "- **Ctrl+C** copia el elemento seleccionado. Si no hay nada seleccionado, copia toda la escena para que puedas pegarla como una escena nueva.\n" +
            "- **Ctrl+X** corta el elemento seleccionado y **Ctrl+V** pega un elemento o una escena copiados. Al pegar una imagen, un enlace o un texto desde otra aplicación, se añade a la escena como un elemento nuevo.\n" +
            "- **Ctrl+Alt+E** duplica el elemento seleccionado.\n" +
            "- **Delete** o **Backspace** elimina el elemento seleccionado, o todos los elementos de una selección múltiple.\n\n" +
            "Mientras escribes en un campo de texto o editas texto, deshacer y copiar/pegar actúan sobre el propio texto, y **Delete** o **Backspace** borran caracteres en lugar del elemento.",
        },
        {
          id: "selection",
          title: "Seleccionar y organizar elementos",
          videoUrl: null,
          text:
            "- **Ctrl+A** selecciona todos los elementos de la escena actual.\n" +
            "- **Ctrl+clic** o **Shift+clic** añade un elemento a la selección o lo quita.\n" +
            "- **Esc** borra una selección de varios elementos.\n" +
            "- Las **teclas de flecha** desplazan el elemento seleccionado un 1 % de la escena por pulsación.\n" +
            "- Mantén pulsada la tecla **Shift** mientras arrastras un tirador de redimensionado para bloquear la relación de aspecto del elemento. Si la relación de aspecto ya está bloqueada, Shift la libera durante ese redimensionado.",
        },
        {
          id: "scenes-timeline",
          title: "Escenas, línea de tiempo y vista previa",
          videoUrl: null,
          text:
            "- En la lista de escenas, **Ctrl+clic** añade una escena a la selección o la quita, **Shift+clic** selecciona todas las escenas entre la última en la que hiciste clic y esta, y **Esc** borra la selección de escenas.\n" +
            "- En la línea de tiempo, **Delete** o **Backspace** elimina el clip seleccionado.\n" +
            "- **F2** abre la vista previa del curso.\n" +
            "- **Ctrl+Shift+P** imprime el curso o lo guarda como PDF desde la ventana de impresión del navegador.",
        },
      ],
    },
  },
};

export default keyboardShortcutsGuide;
